import React, { FC, useEffect } from 'react';
import { filter, get, map } from 'lodash';
import { Trans } from '@lingui/macro';
import { Box, Text, Icon, SpinnerSize, Spinner, Button } from '@bechtel/gds';
import {
  CrewMember,
  RodRequest,
  RodRequestFilterByTerm,
  RodRequestStatusCode,
  FilterOperator,
  Scalars,
} from '@bechtel/types';
import { StencilRodRequestItem } from './item';
import { ListStyled } from '~/request/list/styled';
import { WELDER_ROD_REQUESTS } from '@bechtel/client-gql';
import { useLazyQuery } from '@apollo/client';

export interface StencilRodRequestsProps {
  stencilId: CrewMember['id'];
  onViewRodRequest: (onViewRodRequest: RodRequest) => void;
  selectedRodRequestId?: RodRequest['id'];
  actionCompleted?: boolean;
  setActionCompleted?: React.Dispatch<React.SetStateAction<boolean>>;
  numRodRequests?: Scalars['Int'];
  onClose: () => void;
}
export const StencilRodRequests: FC<StencilRodRequestsProps> = ({
  stencilId,
  onViewRodRequest,
  selectedRodRequestId,
  actionCompleted,
  setActionCompleted,
  numRodRequests,
  onClose,
}) => {
  const pageLimit = 10;
  const [getRodRequests, { data, loading, error, refetch, called, fetchMore }] = useLazyQuery<{
    rodRequests: RodRequest[];
  }>(WELDER_ROD_REQUESTS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
  const rodRequests: RodRequest[] = get(data, 'rodRequests.items', []);
  const rodRequestTotalRecords = get(data, 'rodRequests.total', 0);
  const rodRequestTotalPages = rodRequestTotalRecords ? Math.ceil(rodRequestTotalRecords / pageLimit) : 0;
  const rodRequestCursor = get(data, 'rodRequests.cursor', 1);
  const filteredRodRequests = filter(
    rodRequests,
    (rodrequest) => rodrequest.currentStatusI18n !== RodRequestStatusCode.DELETED
  );

  const handleScroll = (e) => {
    if (
      e.target.scrollTop + e.target.clientHeight + 200 >= e.target.scrollHeight &&
      rodRequestCursor <= rodRequestTotalPages &&
      !loading
    ) {
      fetchMore({
        variables: {
          paging: {
            cursor: rodRequestCursor.toString(),
            limit: pageLimit,
          },
        },
      });
    }
  };
  useEffect(() => {
    if (!stencilId || called) return;
    getRodRequests({
      variables: {
        input: {
          complexFilter: {
            and: [
              {
                term: RodRequestFilterByTerm.ASSIGNEE_ID,
                value: stencilId,
                operator: FilterOperator.EQUALS,
              },
              {
                term: RodRequestFilterByTerm.ROD_REQUEST_STATUS_ID,
                values: [
                  RodRequestStatusCode.REQUESTED,
                  RodRequestStatusCode.PARTIAL_ISSUED,
                  RodRequestStatusCode.EXPIRED,
                ],
                operator: FilterOperator.IN,
              },
            ],
          },
        },
        paging: {
          cursor: '1',
          limit: pageLimit,
        },
      },
    });
  }, [called, getRodRequests, stencilId]);

  useEffect(() => {
    if (!actionCompleted) return;
    if (rodRequests && rodRequests.length <= pageLimit && rodRequestCursor <= rodRequestTotalPages)
      fetchMore({
        variables: {
          paging: {
            cursor: rodRequestCursor.toString(),
            limit: pageLimit,
          },
        },
      });
    if (setActionCompleted) setActionCompleted(false);
  }, [actionCompleted]);

  return (
    <ListStyled
      style={{ height: '100%' }}
      onScroll={(e) => {
        handleScroll(e);
      }}
    >
      {map(filteredRodRequests, (filteredRodRequest) => {
        return (
          <StencilRodRequestItem
            isSelected={selectedRodRequestId === filteredRodRequest.id}
            rodRequest={filteredRodRequest}
            onViewRodRequest={onViewRodRequest}
            key={filteredRodRequest?.id}
          />
        );
      })}
      {numRodRequests == 0 && onClose && onClose()}
      {loading && (
        <Box p={{ sm: '500' }} alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} flexGrow={{ sm: 1 }}>
          <Spinner size={SpinnerSize.LARGE} />
        </Box>
      )}
      {!loading && error && (
        <Box
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'center' }}
          flexGrow={{ sm: 1 }}
          flexDirection={{ sm: 'column' }}
          p={{ sm: '500' }}
        >
          <Box pb={{ sm: '500' }}>
            <Text>
              <Trans>There was a problem loading Rod Requests. Please click the button below to try again.</Trans>
            </Text>
          </Box>
          <Button onClick={() => refetch()}>
            <Box mr={{ sm: '400' }}>
              <Icon icon="undo" />
            </Box>
            <Trans>Retry</Trans>
          </Button>
        </Box>
      )}
    </ListStyled>
  );
};
